import { formatPesoAmount } from '../utils/money';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiFileText, FiLock, FiLogOut, FiShoppingCart } from 'react-icons/fi';
import {
  clearClientPortalToken,
  getClientPortalMe,
  getClientPortalToken,
  listClientPurchaseOrders,
} from '../api/clientPortalApi';
import ClientPortalWorkflowNav from '../components/ClientPortalWorkflowNav';
import DocumentHeader from '../components/DocumentHeader';
import InlineLoading from '../components/InlineLoading';
import {
  getPurchaseOrderActionLabel,
  getPurchaseOrderStatusLabel,
  getPurchaseOrderStatusTone,
  isPurchaseOrderPreparingStatus,
} from '../utils/purchaseOrderStatus';

const formatMoney = (value) => `$${formatPesoAmount(Number(value || 0))}`;

const formatQuantity = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return '0';
  const hasDecimals = Math.abs(amount - Math.round(amount)) > 0.0001;
  return amount.toLocaleString('de-DE', hasDecimals
    ? { minimumFractionDigits: 0, maximumFractionDigits: 3 }
    : undefined);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('es-CL');
};

const getDocumentTypeLabel = (value) => {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'boleta') return 'Boleta';
  if (normalized === 'factura') return 'Factura';
  return 'Comprobante';
};

const getPaymentTermLabel = (value) =>
  value === 'credit' ? 'Credito' : 'Contado';

const getPaymentStatusLabel = (value) => {
  if (value === 'paid') return 'Pagado';
  if (value === 'partial') return 'Parcial';
  return 'Pendiente';
};

const ClientPortalPurchaseOrdersPage = () => {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [orders, setOrders] = useState([]);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadOrders = useCallback(async () => {
    if (!getClientPortalToken()) {
      navigate('/', { replace: true });
      return;
    }
    setLoading(true);
    setError('');
    try {
      const me = await getClientPortalMe();
      if (me?.mustChangePassword) {
        navigate('/client-portal/change-password', { replace: true });
        return;
      }
      const data = await listClientPurchaseOrders();
      setClient(me);
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === 'PASSWORD_CHANGE_REQUIRED') {
        navigate('/client-portal/change-password', { replace: true });
        return;
      }
      if (err?.response?.status === 401) {
        clearClientPortalToken();
        navigate('/', { replace: true });
        return;
      }
      setError(err?.response?.data?.message || 'No se pudieron cargar las ordenes.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleLogout = () => {
    clearClientPortalToken();
    navigate('/', { replace: true });
  };

  const toggleExpanded = (orderId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const sortedOrders = useMemo(
    () => [...orders].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)),
    [orders]
  );

  const renderSaleVoucherPanel = (sale) => {
    if (!sale) return null;
    const taxBreakdown = Array.isArray(sale.taxBreakdown)
      ? sale.taxBreakdown.filter((row) => Number(row?.amount || 0) > 0)
      : [];
    const showTaxTotal = taxBreakdown.length !== 1;
    const hasDiscount = Number(sale.orderDiscountAmount || 0) > 0;
    return (
      <section className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2 font-semibold text-emerald-900">
              <FiFileText />
              Comprobante de venta #{sale.id}
            </div>
            <p className="mt-1 text-xs text-emerald-700">
              {getDocumentTypeLabel(sale.documentType)} · {formatDateTime(sale.createdAt)}
            </p>
          </div>
          <span className="inline-flex w-fit rounded-full bg-white px-2 py-1 text-xs font-semibold text-emerald-800">
            {getPaymentStatusLabel(sale.paymentStatus)}
          </span>
        </div>

        <div className={`mt-3 grid grid-cols-2 gap-2 ${showTaxTotal ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
          <div className="rounded bg-white/80 p-2">
            <p className="text-xs uppercase text-gray-500">Neto</p>
            <p className="font-semibold text-gray-900">{formatMoney(sale.netTotal)}</p>
          </div>
          {showTaxTotal && (
            <div className="rounded bg-white/80 p-2">
              <p className="text-xs uppercase text-gray-500">Impuestos</p>
              <p className="font-semibold text-gray-900">{formatMoney(sale.taxTotal)}</p>
            </div>
          )}
          <div className="rounded bg-white/80 p-2">
            <p className="text-xs uppercase text-gray-500">Total</p>
            <p className="font-semibold text-gray-900">{formatMoney(sale.totalAmount)}</p>
          </div>
          <div className="rounded bg-white/80 p-2">
            <p className="text-xs uppercase text-gray-500">Condicion</p>
            <p className="font-semibold text-gray-900">{getPaymentTermLabel(sale.paymentTerm)}</p>
          </div>
        </div>

        {(hasDiscount || taxBreakdown.length > 0 || Number(sale.balance || 0) > 0) && (
          <div className="mt-3 space-y-1 rounded bg-white/70 p-2 text-xs text-gray-700">
            {hasDiscount && (
              <p>
                Descuento total: <span className="font-semibold">-{formatMoney(sale.orderDiscountAmount)}</span>
                {sale.orderDiscountPercentage ? ` (${sale.orderDiscountPercentage}%)` : ''}
              </p>
            )}
            {taxBreakdown.map((row, index) => (
              <p key={`${row.taxId || row.name || 'tax'}-${index}`}>
                {row.name || 'Impuesto'}{row.rate ? ` (${row.rate}%)` : ''}: <span className="font-semibold">{formatMoney(row.amount)}</span>
              </p>
            ))}
            {Number(sale.balance || 0) > 0 && (
              <p>
                Pendiente: <span className="font-semibold">{formatMoney(sale.balance)}</span>
              </p>
            )}
          </div>
        )}
      </section>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <InlineLoading label="Cargando ordenes" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <DocumentHeader
        stickyOffsetClassName="top-0"
        title="Mis órdenes de compra"
        showCartButton={false}
        headerActions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/client-portal/change-password')}
              className="inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <FiLock />
              Contraseña
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <FiLogOut />
              Salir
            </button>
          </div>
        }
        clientName={client?.name || ''}
        clientRut={client?.rut || 'Cliente'}
        showClientType={false}
        emptyClientLabel={client?.name || 'Cliente'}
      />

      <main className="mx-auto max-w-6xl space-y-4 p-4">
        <ClientPortalWorkflowNav
          activeKey="orders"
          onNavigate={navigate}
        />

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {sortedOrders.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-gray-600">
            <p>No hay órdenes de compra registradas.</p>
            <button
              type="button"
              onClick={() => navigate('/client-portal/purchase-orders/new')}
              className="mt-4 inline-flex items-center justify-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              <FiShoppingCart />
              Crear orden de compra
            </button>
          </div>
        ) : (
          sortedOrders.map((order) => {
            const isExpanded = expandedIds.has(order.id);
            const items = [...(order.Items || [])].sort((a, b) => Number(a.lineIndex || 0) - Number(b.lineIndex || 0));
            const canEditOrder = !order.purchaseId && isPurchaseOrderPreparingStatus(order.status);
            return (
              <article key={order.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Orden #{order.id}</h2>
                    <p className="text-sm text-gray-600">Actualizada: {formatDateTime(order.updatedAt)}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex rounded-full border px-2 py-1 text-xs ${getPurchaseOrderStatusTone(order.status)}`}>
                      {getPurchaseOrderStatusLabel(order.status)}
                    </span>
                    {canEditOrder && (
                      <button
                        type="button"
                        onClick={() => navigate(`/client-portal/purchase-orders/${order.id}/edit`)}
                        className="inline-flex items-center gap-1 rounded bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700"
                      >
                        <FiEdit2 />
                        Editar
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => toggleExpanded(order.id)}
                      className="rounded bg-gray-100 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200"
                    >
                      {isExpanded ? 'Ocultar' : 'Ver detalle'}
                    </button>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
                  <div className="rounded bg-gray-50 p-3">
                    <p className="text-xs uppercase text-gray-500">Items</p>
                    <p className="font-semibold">{order.confirmedItemCount || 0}/{order.itemCount || 0}</p>
                  </div>
                  <div className="rounded bg-gray-50 p-3">
                    <p className="text-xs uppercase text-gray-500">Solicitado</p>
                    <p className="font-semibold">{formatMoney(order.requestedTotalAmount)}</p>
                  </div>
                  <div className="rounded bg-gray-50 p-3">
                    <p className="text-xs uppercase text-gray-500">Actual</p>
                    <p className="font-semibold">{formatMoney(order.currentTotalAmount)}</p>
                  </div>
                  <div className="rounded bg-gray-50 p-3">
                    <p className="text-xs uppercase text-gray-500">Creada</p>
                    <p className="font-semibold">{formatDateTime(order.createdAt)}</p>
                  </div>
                </div>

                {isExpanded && (
                  <>
                    <div className="mt-4 overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Producto</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">Solicitado</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">Actual</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">Subtotal</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Estado</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {items.map((item) => (
                            <tr key={item.id}>
                              <td className="px-3 py-2">
                                <div className="font-medium text-gray-900">{item.productName || 'Producto'}</div>
                                <div className="text-xs text-gray-500">{item.varietyName || ''} {item.metricUnit || ''}</div>
                                {item.adjustmentReasonLabel && (
                                  <div className="text-xs text-gray-500">Motivo: {item.adjustmentReasonLabel}</div>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right">{formatQuantity(item.requestedQuantity)}</td>
                              <td className="px-3 py-2 text-right">{formatQuantity(item.currentQuantity)}</td>
                              <td className="px-3 py-2 text-right font-semibold">{formatMoney(item.currentSubtotal)}</td>
                              <td className="px-3 py-2">{getPurchaseOrderActionLabel(item.confirmationAction)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {renderSaleVoucherPanel(order.SaleSummary)}
                  </>
                )}
              </article>
            );
          })
        )}
      </main>
    </div>
  );
};

export default ClientPortalPurchaseOrdersPage;
